/**
 * Shared types for the offline subsystem.
 *
 * Kept apart from `db.ts` so they can be imported by hooks and
 * UI components without dragging the IndexedDB module along —
 * matters because `idb-keyval` immediately probes
 * `window.indexedDB`, which would throw in unit-test setups.
 */

/**
 * The JSON body posted to `POST /api/missions`. Mirrors
 * `apps/api/src/modules/missions/missions.schema.ts.createMissionSchema`
 * but kept as a plain interface so we don't import from the API
 * package (the PWA must build standalone).
 */
export interface PendingMissionPayload {
  driverId: string
  site: string
  date: string
  clientId: string | null
  vehicleId: string | null
  slots: Array<{ start: string; end: string }>
  washing: boolean
  travel: string
  isDaily: boolean
  tollAmount?: number
  planningId?: string
}

/**
 * One queued mission waiting to be flushed to the server.
 *
 * `signatures` and `receiptPhoto` carry actual `Blob` / `File`
 * objects — IndexedDB stores them natively without any base64
 * round-trip. That's the whole reason we picked IDB over
 * `localStorage` for the queue.
 *
 * `status` drives the UI: `pending` rows wait their turn,
 * `syncing` rows are being uploaded right now, `error` rows have
 * exhausted automatic retries and need a manual "Réessayer".
 */
export interface PendingMission {
  localId: string
  payload: PendingMissionPayload
  signatures: {
    supervisor: Blob | null
    driver: Blob | null
  }
  receiptPhoto: File | null
  /** Epoch ms when the driver hit "Envoyer". Used for FIFO ordering. */
  createdAt: number
  /** Number of failed automatic upload attempts so far. */
  retryCount: number
  /** Last server / network error message, surfaced in the UI. */
  lastError: string | null
  status: 'pending' | 'syncing' | 'error'
}

/**
 * Snapshot of one synchronisation pass — what the controller
 * returns after walking the queue.
 */
export interface SyncResult {
  /** How many missions were successfully uploaded. */
  succeeded: number
  /** How many missions failed (will be retried later). */
  failed: number
  /** How many missions exhausted retries and are now in `error`. */
  exhausted: number
}

/** Hard ceiling on auto-retries before requiring manual intervention. */
export const MAX_AUTO_RETRIES = 5
